import { AvatarProps, selectUser, sortString } from '@centerpiece/components';
import { useItemContext } from 'contexts/item.context';
import { useProjectContext } from 'contexts/project.context';
import { useSelector } from 'react-redux';
import {
  useGetProjectContactsQuery,
  useGetContactsQuery,
  useGetSubscriptionContactsQuery,
  useGetOrganizationsQuery,
} from 'store/api';
import {
  ProjectContact,
  Contact,
  Organization,
  SubscriptionContact,
  Role,
  Subscriber,
  Relationship,
} from 'store/api/models';

// Helper function to group contacts by organization
export const groupContactsByOrganization = (
  contacts: SubscriptionContact[],
  organizations: Organization[]
) => {
  const organizationMap: any = {};
  organizations.forEach(o => (organizationMap[o.name] = []));
  contacts?.forEach(contact => {
    if (!organizationMap[contact.organization_r?.name || '']) {
      organizationMap[contact.organization_r?.name || ''] = [];
    }
    organizationMap[contact.organization_r?.name || ''].push(contact);
  });
  Object.keys(organizationMap)?.forEach(
    (organization: string) =>
      (organizationMap[organization] = [
        ...(organizationMap[organization] || []),
      ].sort((a: any, b: any) =>
        sortString(
          `${a.contact_r?.firstname} ${a.contact_r?.lastname}`,
          `${b.contact_r?.firstname} ${b.contact_r?.lastname}`
        )
      ))
  );

  return organizationMap;
};

// Helper function to group contacts by organization
export const groupOrganizationContacts = (
  contacts: SubscriptionContact[],
  organizations: Organization[]
) => {
  const organizationMap: {
    [key: string]: { organization: Organization; contacts: any[] };
  } = {};
  organizations.forEach(
    o => (organizationMap[o.name] = { organization: o, contacts: [] })
  );
  contacts?.forEach(contact => {
    if (!organizationMap[contact.organization_r?.name || '']) {
      organizationMap[contact.organization_r?.name || ''] = {
        organization: {} as Organization,
        contacts: [],
      };
    }
    organizationMap[contact.organization_r?.name || ''].contacts.push(contact);
  });
  Object.keys(organizationMap)?.forEach(
    (organization: string) =>
      (organizationMap[organization].contacts = [
        ...(organizationMap[organization].contacts || []),
      ].sort((a: any, b: any) =>
        sortString(
          `${a.contact_r?.firstname} ${a.contact_r?.lastname}`,
          `${b.contact_r?.firstname} ${b.contact_r?.lastname}`
        )
      ))
  );

  return organizationMap;
};

// Helper function to group contacts by role
export const groupContactsByRole = (
  contacts: SubscriptionContact[],
  organizations: Organization[]
) => {
  const roleMap: { [key: string]: { organizations: any[]; contacts: any[] } } =
    {};
  organizations.forEach(organization => {
    if (!roleMap[organization?.role_r?.name || ' ']) {
      roleMap[organization?.role_r?.name || ' '] = {
        organizations: [],
        contacts: [],
      };
    }
    roleMap[organization?.role_r?.name || ' '].organizations.push(organization);
  });
  contacts?.forEach(contact => {
    if (!roleMap[contact.role_r?.name || ' ']) {
      roleMap[contact.role_r?.name || ' '] = {
        organizations: [],
        contacts: [],
      };
    }
    roleMap[contact.role_r?.name || ' '].contacts.push(contact);
  });
  Object.keys(roleMap)?.forEach((role: string) => {
    roleMap[role].organizations = [...(roleMap[role].organizations || [])].sort(
      (a: any, b: any) => sortString(a.name, b.name)
    );
    roleMap[role].contacts = [...(roleMap[role].contacts || [])].sort(
      (a: any, b: any) =>
        sortString(
          `${a.contact_r?.firstname} ${a.contact_r?.lastname}`,
          `${b.contact_r?.firstname} ${b.contact_r?.lastname}`
        )
    );
  });

  return roleMap;
};

export const useAllContacts = () => {
  const { id } = useProjectContext();
  const { project_id } = useItemContext();
  const user = useSelector(selectUser);
  const { data: project_contacts = [] } = useGetProjectContactsQuery(
    {
      params: { project: id || project_id, nested: 'all' },
    },
    { skip: !(id || project_id) || !user.contact }
  );
  const { data: contacts = [] } = useGetContactsQuery(
    {},
    { skip: !(id || project_id) || !user.contact }
  );
  return [
    ...[...project_contacts]
      ?.filter((pc: ProjectContact) => pc.contact !== user.contact)
      ?.sort((a: ProjectContact, b: ProjectContact) => {
        const fa =
          `${a?.contact_r?.firstname} ${a?.contact_r?.lastname}`.toLowerCase();
        const fb =
          `${b?.contact_r?.firstname} ${b?.contact_r?.lastname}`.toLowerCase();
        if (fa < fb) {
          return -1;
        }
        if (fa > fb) {
          return 1;
        }
        return 0;
      })
      ?.map((contact: ProjectContact) => {
        return {
          ...contact?.contact_r,
          name: `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`,
          organization: contact?.organization,
          role: contact?.role_r?.name,
          groupBy: 'Project Contacts',
        };
      }),
    ...[...contacts]
      // ?.filter((contact: Contact) => contact.id !== user.contact)
      ?.sort((a: Contact, b: Contact) => {
        const fa = a?.organization_r?.name?.toLowerCase();
        const fb = b?.organization_r?.name?.toLowerCase();
        if (fa < fb || (!fb && !!fa)) {
          return -1;
        }
        if (fa > fb || (!fa && !!fb)) {
          return 1;
        }
        return 0;
      })
      ?.map((contact: Contact) => {
        return {
          ...contact,
          name: `${contact?.firstname} ${contact?.lastname}`,
          role: contact?.role_r?.name,
          organization: contact?.organization,
        };
      }),
  ];
};

export const stakeHolderOptions = ({
  subscription_contacts,
  organizations,
  role,
}: {
  subscription_contacts: SubscriptionContact[];
  organizations: Organization[];
  role?: Role;
}) => {
  // const groupedOrgs = groupOrganizationContacts(
  //   subscription_contacts.filter(sc => sc?.role_r?.id === role?.id),
  //   organizations.filter(o => o?.role_r?.id === role?.id)
  // );
  const groupedOrgs = groupOrganizationContacts(
    subscription_contacts,
    organizations
  );
  let soptions: any[] = [];
  [...(Object.keys(groupedOrgs) || [])].sort().forEach(org => {
    if (!groupedOrgs[org]?.organization?.id) {
      soptions.push(...groupedOrgs[org].contacts);
    } else {
      soptions.push(
        {
          ...groupedOrgs[org].organization,
          organization_r: { ...groupedOrgs[org]?.organization },
          organization: groupedOrgs[org]?.organization?.id,
          type: 'organization',
        },
        ...groupedOrgs[org].contacts?.map(c => ({
          ...c,
          organization_r: { ...groupedOrgs[org]?.organization },
          organization: groupedOrgs[org]?.organization?.id,
        }))
      );
    }
  });
  let roptions: any[] = [];
  if (role) {
    let roleOrgs: {
      [key: string]: { organization: Organization; contacts: any[] };
    } = {};
    Object.keys(groupedOrgs).forEach(o => {
      if (groupedOrgs[o]?.organization?.role_r?.id === role?.id) {
        roleOrgs[o] = {
          organization: {
            ...groupedOrgs[o]?.organization,
            groupBy: role?.name,
          } as any,
          contacts: [...groupedOrgs[o]?.contacts],
        };
      } else {
        const roleContacts = groupedOrgs[o]?.contacts
          .filter(sc => sc?.role_r?.id === role?.id)
          .map(sc => ({ ...sc, groupBy: role?.name }));
        if (roleContacts.length > 0) {
          roleOrgs[o] = {
            organization: groupedOrgs[o]?.organization,
            contacts: roleContacts,
          };
        }
      }
    });

    [...(Object.keys(roleOrgs) || [])].sort().forEach(org => {
      roptions.push(
        { ...roleOrgs[org].organization, type: 'organization' },
        ...roleOrgs[org].contacts
      );
    });
  }
  return [
    ...roptions.map(r => ({ ...r, group: role?.name })),
    ...soptions.map(s => ({ ...s, group: 'All' })),
  ];
};

export const internalRoleHolderOptions = ({
  subscribers,
  role,
}: {
  subscribers: Subscriber[];
  role?: Role;
}) => {
  let soptions: any[] = subscribers;

  let roptions: any[] = [];
  if (role) {
    roptions = subscribers.filter(s => s?.role_r?.id === role?.id);
  }
  return [
    ...roptions.map(r => ({ ...r, group: role?.name })),
    ...soptions.map(s => ({ ...s, group: 'All' })),
  ];
};

export const useTransformRelationships = (data: Relationship[]) => {
  const { contacts } = useProjectContext();
  return [...data]
    .map((rel: Relationship) => {
      if (rel.contacts && rel.contacts.length > 0) {
        return rel.contacts.map(
          (c: Contact) =>
            ({
              id: rel.id,
              contact_r: contacts[c.id],
              variant: rel.role_r ? 'rounded' : 'circular',
              role_r: rel.role_r,
              group: 'subscriber',
            } as Relationship)
        );
      } else if (rel.contact) {
        return {
          ...rel,
          contact_r: contacts[rel.contact] || rel.contact_r,
          variant: 'circular',
          group: 'subscriber',
        } as Relationship;
      } else if (rel.role_r) {
        return {
          ...rel,
          variant: 'square',
          group: 'role',
        } as Relationship;
      } else {
        return { ...rel } as Relationship;
      }
    })
    .flat();
};

export const transformRelationshipsFromContacts = (
  data: Relationship[],
  contacts: { [key: number]: Contact }
) => {
  return [...data]
    .map((rel: Relationship) => {
      if (rel.contacts && rel.contacts.length > 0) {
        return rel.contacts.map(
          (c: Contact) =>
            ({
              id: rel.id,
              contact_r: contacts[(c as any).contact],
              variant: rel.role_r ? 'rounded' : 'circular',
              role_r: rel.role_r,
              group: 'subscriber',
            } as Relationship)
        );
      } else if (rel.contact) {
        return {
          ...rel,
          contact_r: contacts[rel.contact],
          variant: 'circular',
          group: 'subscriber',
        } as Relationship;
      } else if (rel.role_r) {
        return {
          ...rel,
          variant: 'square',
          group: 'role',
        } as Relationship;
      } else {
        return { ...rel } as Relationship;
      }
    })
    .flat();
};

export const transformRelationships = (data: Relationship[]) => {
  return [...data]
    .map((rel: Relationship) => {
      if (rel.contacts && rel.contacts.length > 0) {
        return rel.contacts.map(
          (contact_r: Contact) =>
            ({
              id: rel.id,
              contact_r: contact_r,
              variant: rel.role_r ? 'rounded' : 'circular',
              role_r: rel.role_r,
              group: 'subscriber',
            } as Relationship)
        );
      } else if (rel.contact_r) {
        return {
          ...rel,
          variant: 'circular',
          group: 'subscriber',
        } as Relationship;
      } else if (rel.role_r) {
        return {
          ...rel,
          variant: 'square',
          group: 'role',
        } as Relationship;
      } else {
        return { ...rel } as Relationship;
      }
    })
    .flat();
};

export const relationshipOptions = ({
  template,
  roles,
  project_contacts,
  subscribers,
}: {
  template?: boolean;
  roles: Role[];
  project_contacts: ProjectContact[];
  subscribers: Subscriber[];
}) => {
  return (data: Relationship[]) =>
    (template
      ? roles
          ?.filter(r => r.type === 'Internal')
          ?.map(r => ({ ...r, role_r: { ...r }, group: 'role' }))
      : [
          ...(roles || [])
            ?.filter(r => r.type === 'Internal')
            ?.map(r => ({ ...r, role_r: { ...r }, group: 'role' })),
          ...(project_contacts || [])?.map((pc: ProjectContact) => ({
            ...pc,
            ...pc?.contact_r,
            group: 'project_contact',
            contact: pc?.contact_r?.id,
            role: pc?.role_r,
            name:
              pc?.contact_r?.firstname && pc?.contact_r?.firstname !== ''
                ? `${pc.contact_r?.firstname} ${pc.contact_r?.lastname}`
                : pc.contact_r?.email,
          })),
          ...(subscribers || [])
            ?.filter(
              s => !project_contacts?.find(pc => pc.contact === s.contact_r?.id)
            )
            ?.map((subscriber: Subscriber) => ({
              ...subscriber,
              ...subscriber?.contact_r,
              group: 'subscriber',
              contact: subscriber?.contact_r?.id,
              name:
                subscriber?.contact_r?.firstname &&
                subscriber?.contact_r?.firstname !== ''
                  ? `${subscriber.contact_r?.firstname} ${subscriber.contact_r?.lastname}`
                  : subscriber.contact_r?.email,
            })),
          roles
            ?.filter(r => r.type === 'Internal')
            ?.map(r => ({ ...r, role_r: { ...r }, group: 'role' })),
        ]
    )?.filter(
      (s: any) =>
        !data?.find(
          (r: any) =>
            ((r?.contact || r?.contacts?.length > 0) &&
              s?.contact_r?.id === r?.contact_r?.id) ||
            (s?.role_r && r?.role_r && s?.role_r?.id === r?.role_r?.id)
        )
    ) || [];
};
